<template>
    <div class="two-option-large-menu" :class="{ 'no-hover': !isStylePreview && !isPreview, 'inline-edit-mode': enableInlineEdit }" :style="densityVars">
        <div class="frame" :style="frameStyles">
            <div class="back-btn" :style="backBtnStyles">
                <div class="back-icon-wrapper" :style="backIconStyles">
                    <component :is="icons.back2" class="back-icon" :color="backIconColor" />
                </div>
            </div>

            <div
                class="title-bar"
                :style="titleBarStyles"
                :class="{ 'clickable-title': enableInlineEdit }"
                @click="enableInlineEdit ? focusTitleText($event) : null"
            >
                <div
                    ref="titleTextRef"
                    class="title-text editable-text"
                    :class="{ 'is-editable': enableInlineEdit }"
                    :style="titleStyles"
                    :contenteditable="enableInlineEdit"
                    @blur="updateTitle"
                    @input="(e) => handleTextFieldInput('titleText', e)"
                    @paste.prevent="handlePaste"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ displayTitleText }}</div>
            </div>

            <div class="cards" :style="cardsStyles">
                <div
                    v-for="(card, i) in optionCards"
                    :key="i"
                    class="card-wrap"
                    :style="cardWrapStyles"
                >
                    <div class="card" :style="cardStyles">
                        <div
                            :ref="`labelRef${i}`"
                            v-show="true"
                            class="card-label editable-text"
                            :class="{ 'is-editable': enableInlineEdit }"
                            :style="getCardLabelStyles(card)"
                            :contenteditable="enableInlineEdit"
                            @blur="(e) => updateCardLabel(i, e)"
                            @input="(e) => handleTextFieldInput(`label${i}`, e)"
                            @paste.prevent="handlePaste"
                            @mousedown="handleEditableMouseDown"
                            @keydown.enter.prevent="handleEnter"
                            suppressContentEditableWarning
                        >{{ getDisplayLabel(i) }}</div>

                        <div
                            class="image-placeholder"
                            :class="{ 'image-clickable': enableInlineEdit && (!card.imageType || card.imageType === 'image') }"
                            :style="getImagePlaceholderStyle(card)"
                            @click="enableInlineEdit && (!card.imageType || card.imageType === 'image') ? handleImageClick(i) : null"
                        >
                            <div v-if="card.imageType === 'icon' && card.selectedIcon" class="icon-display">
                                <component :is="getIconComponent(card.selectedIcon)" :style="iconDisplayStyles" class="icon-img" v-if="getIconComponent(card.selectedIcon)" />
                            </div>
                            <div v-else-if="(card.imageType === 'image' || card.imageType === 'resource') && card.imageUrl" class="image-display">
                            </div>
                            <div v-else :style="placeholderTextStyles">{{ card.placeholderText }}</div>
                        </div>
                    </div>

                    <div
                        v-show="true"
                        :ref="`buttonRef${i}`"
                        class="select editable-text"
                        :class="{ 'is-editable': enableInlineEdit }"
                        :style="getSelectButtonStyles(card)"
                        :contenteditable="enableInlineEdit"
                        @click="handleCardButtonClick(i, card)"
                        @blur="(e) => updateCardButton(i, e)"
                        @input="(e) => handleTextFieldInput(`buttonText${i}`, e)"
                        @paste.prevent="handlePaste"
                        @mousedown="handleEditableMouseDown"
                        @keydown.enter.prevent="handleEnter"
                        suppressContentEditableWarning
                    >{{ getDisplayButtonText(i) }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import icons from './menu-icons';
import menuMixin from './menu-mixin';
import menuReactivityMixin from './menu-reactivity-mixin';
const Back2SVG = require('!raw-loader!../assets/icons/back2.svg');

const IMAGE_PLACEHOLDER_STYLES = {
    width: '100%',
    aspectRatio: '1',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px',
    backgroundColor: 'transparent',
    marginTop: 'auto',
    marginBottom: 'auto'
};

export default {
    name: "MenuTwoOptionLarge",
    mixins: [menuMixin, menuReactivityMixin],
    props: {
        styleData: {type: Object, default: () => ({})},
        menuData: {type: Object, default: () => ({})},
        isStylePreview: {type: Boolean, default: false},
        isPreview: {type: Boolean, default: false},
        enableInlineEdit: {type: Boolean, default: false}
    },
    data() {
        return {
            icons: icons
        };
    },
    computed: {
        titleText() {
            return this.cleanText(this.menuData.titleText);
        },
        displayTitleText() {
            const text = this.titleText;
            if (!text) {
                if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                    return "Two Option Menu Title Goes Here";
                }
                return this.enableInlineEdit ? ' ' : '';
            }
            return text;
        },
        optionCards() {
            const defaultCard = {
                label: "",
                placeholderText: "THIS WILL BE AN OPTIONAL IMAGE OR PRESET ICON.",
                buttonText: "",
                imageType: "icon",
                selectedIcon: null,
                imageUrl: null,
                zoom: 1,
                offsetX: 0,
                offsetY: 0
            };

            let cards = [];
            if (this.menuData.optionCards && this.menuData.optionCards.length > 0) {
                cards = this.menuData.optionCards.map((card) => ({
                    ...defaultCard,
                    ...card
                }));
            } else {
                cards = [defaultCard, defaultCard];
            }

            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                return cards.map((card, index) => {
                    const defaultLabels = ["Option A", "Option B"];
                    const defaultButtons = ["Select", "Select"];

                    return {
                        ...card,
                        label: (card.label && card.label.trim()) || defaultLabels[index],
                        buttonText: (card.buttonText && card.buttonText.trim()) || defaultButtons[index]
                    };
                });
            }

            return cards;
        },
        visibleLabelTexts() {
            if (!this.optionCards || !Array.isArray(this.optionCards)) {
                return [];
            }
            const defaultLabels = ["Option A", "Option B"];
            return this.optionCards.map((card, index) => {
                const cleaned = this.cleanText(card?.label);
                if (!cleaned) {
                    if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                        return defaultLabels[index] || '';
                    }
                    return this.enableInlineEdit ? ' ' : '';
                }
                return cleaned;
            });
        },
        visibleButtonTexts() {
            if (!this.optionCards || !Array.isArray(this.optionCards)) {
                return [];
            }
            const defaultButtons = ["Select", "Select"];
            return this.optionCards.map((card, index) => {
                const cleaned = this.cleanText(card?.buttonText);
                if (!cleaned) {
                    if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                        return defaultButtons[index] || '';
                    }
                    return this.enableInlineEdit ? ' ' : '';
                }
                return cleaned;
            });
        },

        densityVars() {
            if (!this.isStylePreview) return {};
            return {
                "--base-font": "0.75rem",
                "--title-scale": "1.3",
                "--card-scale": "0.75",
                "--max-width-scale": "0.65",
                "--element-scale": "0.75",
                "--gap-scale": "0.8"
            };
        },

        frameStyles() {
            const maxWidth = this.isStylePreview ? "calc(900px * var(--max-width-scale, 1))" : "900px";
            const columnGap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "16px";
            const rowGap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "16px";
            const margin = this.isStylePreview ? "calc(16px * var(--gap-scale, 1)) auto" : "16px auto";
            const transform = this.isPreview ? "translateX(5%)" : "none";
            const backBtnCol = this.isStylePreview ? "calc(66px * var(--element-scale, 1))" : "75px";
            return {
                display: "grid",
                gridTemplateColumns: `${backBtnCol} 1fr`,
                columnGap: columnGap,
                rowGap: rowGap,
                width: "100%",
                maxWidth: maxWidth,
                margin: margin,
                transform: transform
            };
        },

        backBtnStyles() {
            const width = this.isStylePreview ? "calc(66px * var(--element-scale, 1))" : "75px";
            const height = this.isStylePreview ? "calc(56px * var(--element-scale, 1))" : "56px";
            const fontSize = this.isStylePreview ? "calc(22px * var(--element-scale, 1))" : "22px";
            const isBackButtonEnabled = this.menuData?.backButton?.enabled !== false;
            return {
                width: width,
                height: height,
                borderRadius: '6px',
                display: isBackButtonEnabled ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: fontSize,
                fontWeight: 'bold',
                boxShadow: '0 4px 10px rgba(0,0,0,0.35)',
                border: '3px solid',
                transition: 'background-color .18s ease, box-shadow .18s ease',
                backgroundColor: this.rgba(this.effectiveStyleData.buttonBackgroundColor),
                color: this.rgba(this.effectiveStyleData.textColor, false),
                borderColor: this.rgba(this.effectiveStyleData.outlineColor, false),
                '--hover-bg': this.rgba(this.effectiveStyleData.buttonHoverColor)
            };
        },
        backIconStyles() {
            return this.getIconStylesFromSvg(Back2SVG);
        },
        backIconColor() {
            const effective = this.getEffectiveStyle();
            return this.rgba(effective.textColor, false);
        },

        titleBarStyles() {
            const totalCardsWidth = this.isStylePreview ?
                `calc((220px * var(--card-scale, 1)) * 2 + (24px * var(--gap-scale, 1)) * 1)` :
                "calc(280px * 2 + 24px * 1)";

            const barH = this.isStylePreview ? "calc(56px * var(--element-scale, 1))" : "56px";
            const padding = this.isStylePreview ? "0 calc(18px * var(--element-scale, 1))" : "0 18px";
            const hidden = this.isTitleHidden();
            return {
                gridColumn: '2 / -1',
                width: totalCardsWidth,
                height: barH,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: padding,
                borderRadius: '8px',
                boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
                border: '3px solid',
                backgroundColor: this.rgba(this.effectiveStyleData.titleScreenBackgroundColor),
                borderColor: this.rgba(this.effectiveStyleData.outlineColor, false),
                visibility: hidden ? 'hidden' : 'visible',
                pointerEvents: hidden ? 'none' : 'auto'
            };
        },
        titleStyles() {
            return {
                margin: 0,
                fontWeight: 800,
                letterSpacing: "0.4px",
                fontFamily: 'inherit',
                fontSize: "calc(var(--base-font) * var(--title-scale))",
                lineHeight: 1,
                color: this.rgba(this.effectiveStyleData.titleColor, false),
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
            };
        },

        cardsStyles() {
            const gap = this.isStylePreview ? "calc(24px * var(--gap-scale, 1))" : "24px";
            return {
                gridColumn: '2 / -1',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: gap,
                flexWrap: this.isStylePreview ? 'nowrap' : 'wrap',
                width: '100%'
            };
        },

        cardWrapStyles() {
            const gap = this.isStylePreview ? "calc(10px * var(--gap-scale, 1))" : "10px";
            return {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: gap,
                minWidth: 0
            };
        },

        cardStyles() {
            const width = this.isStylePreview ? "calc(220px * var(--card-scale, 1))" : "280px";
            const minHeight = this.isStylePreview ? "calc(280px * var(--card-scale, 1))" : "360px";
            const padding = this.isStylePreview ? "calc(12px * var(--element-scale, 1))" : "16px";
            return {
                width: width,
                minWidth: 0,
                minHeight: minHeight,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                border: '3px solid',
                padding: padding,
                boxShadow: '0 8px 18px rgba(0,0,0,0.4)',
                backgroundColor: this.rgba(this.effectiveStyleData.titleScreenBackgroundColor),
                borderColor: this.rgba(this.effectiveStyleData.outlineColor, false)
            };
        },

        cardTitleStyles() {
            const fontSize = this.isStylePreview ? "calc(24px * var(--element-scale, 1))" : "24px";
            const marginBottom = this.isStylePreview ? "calc(12px * var(--gap-scale, 1))" : "12px";
            return {
                fontSize: fontSize,
                fontWeight: 700,
                marginBottom: marginBottom,
                textAlign: 'left',
                fontFamily: 'inherit',
                color: this.rgba(this.effectiveStyleData.textColor, false)
            };
        },

        selectButtonStyles() {
            const width = this.isStylePreview ? "calc(220px * var(--card-scale, 1))" : "280px";
            const padding = this.isStylePreview ? "calc(8px * var(--element-scale, 1)) calc(12px * var(--element-scale, 1))" : "12px 18px";
            const fontSize = this.isStylePreview ? "calc(18px * var(--element-scale, 1))" : "16px";
            return {
                width: width,
                padding: padding,
                borderRadius: '999px',
                border: '3px solid',
                fontSize: fontSize,
                fontWeight: 600,
                letterSpacing: '0.4px',
                cursor: 'pointer',
                textAlign: 'center',
                boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
                backgroundColor: this.rgba(this.effectiveStyleData.buttonBackgroundColor),
                color: this.rgba(this.effectiveStyleData.buttonTextColor, false),
                borderColor: this.rgba(this.effectiveStyleData.outlineColor, false),
                '--hover-bg': this.rgba(this.effectiveStyleData.buttonHoverColor)
            };
        },

        placeholderTextStyles() {
            const fontSize = this.isStylePreview ? "calc(13px * var(--element-scale, 1))" : "13px";
            return {
                fontSize: fontSize,
                textAlign: 'center',
                textTransform: 'uppercase',
                letterSpacing: '1.1px',
                lineHeight: '1.3',
                fontFamily: 'ui-monospace, monospace',
                color: 'rgba(243,173,198,0.95)'
            };
        },

        effectiveStyleData() {
            const d = {
                titleScreenBackgroundColor: {r: 44, g: 62, b: 80, a: 1},
                titleColor: {r: 255, g: 255, b: 255, a: 1},
                textColor: {r: 235, g: 244, b: 248, a: 0.95},
                iconColor: {r: 235, g: 244, b: 248, a: 0.95},
                buttonBackgroundColor: {r: 33, g: 122, b: 145, a: 0.95},
                buttonHoverColor: {r: 52, g: 192, b: 32, a: 1},
                buttonTextColor: {r: 255, g: 255, b: 255, a: 1},
                outlineColor: {r: 255, g: 255, b: 255, a: 0.9},
                opacity: 1.0
            };
            return {...d, ...this.styleData};
        },

        iconDisplayStyles() {
            return {
                width: '180px',
                height: '180px',
                objectFit: 'contain',
                color: this.rgba(this.effectiveStyleData.iconColor, false)
            };
        }
    },
    watch: {
        displayTitleText(newVal) {
            this.syncEditableText('titleTextRef', newVal);
        },
        visibleLabelTexts: {
            handler(newTexts) {
                if (Array.isArray(newTexts)) {
                    this.$nextTick(() => {
                        newTexts.forEach((displayText, index) => {
                            this.syncLabelTextContent(index, displayText);
                        });
                    });
                }
            },
            immediate: true
        },
        visibleButtonTexts: {
            handler(newTexts) {
                if (Array.isArray(newTexts)) {
                    this.$nextTick(() => {
                        newTexts.forEach((displayText, index) => {
                            this.syncButtonTextContent(index, displayText);
                        });
                    });
                }
            },
            immediate: true
        },
        enableInlineEdit: {
            handler(enabled) {
                if (enabled) {
                    this.$nextTick(() => {
                        if (Array.isArray(this.visibleLabelTexts)) {
                            this.visibleLabelTexts.forEach((displayText, index) => {
                                this.syncLabelTextContent(index, displayText);
                            });
                        }
                        if (Array.isArray(this.visibleButtonTexts)) {
                            this.visibleButtonTexts.forEach((displayText, index) => {
                                this.syncButtonTextContent(index, displayText);
                            });
                        }
                    });
                }
            }
        }
    },
    methods: {
        getIconComponent(iconValue) {
            return this.icons[iconValue] || null;
        },
        getDisplayLabel(index) {
            const text = this.visibleLabelTexts[index] || '';
            return this.enableInlineEdit && !text ? ' ' : text;
        },
        getDisplayButtonText(index) {
            const text = this.visibleButtonTexts[index] || '';
            return this.enableInlineEdit && !text ? ' ' : text;
        },
        getSelectButtonStyles(card) {
            const width = this.isStylePreview ? "calc(220px * var(--card-scale, 1))" : "280px";
            const padding = this.isStylePreview ? "calc(8px * var(--element-scale, 1)) calc(12px * var(--element-scale, 1))" : "12px 18px";
            const fontSize = this.isStylePreview ? "calc(18px * var(--element-scale, 1))" : "16px";
            const hidden = this.isCardButtonHidden(card);
            return {
                width: width,
                padding: padding,
                borderRadius: '999px',
                border: '3px solid',
                fontSize: fontSize,
                fontWeight: 600,
                letterSpacing: '0.4px',
                cursor: hidden ? 'default' : 'pointer',
                textAlign: 'center',
                boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                backgroundColor: this.rgba(this.effectiveStyleData.buttonBackgroundColor),
                color: this.rgba(this.effectiveStyleData.buttonTextColor, false),
                borderColor: this.rgba(this.effectiveStyleData.outlineColor, false),
                '--hover-bg': this.rgba(this.effectiveStyleData.buttonHoverColor),
                visibility: 'visible',
                pointerEvents: 'auto'
            };
        },
        getCardLabelStyles(card) {
            const fontSize = this.isStylePreview 
                ? "calc(14px * var(--element-scale, 1))" 
                : "calc(var(--base-font) * var(--title-scale))";
            const marginBottom = this.isStylePreview ? "calc(8px * var(--gap-scale, 1))" : "8px";
            return {
                fontSize: fontSize,
                fontWeight: 600,
                marginBottom: marginBottom,
                textAlign: 'center',
                letterSpacing: '1.5px',
                fontFamily: 'inherit',
                color: this.rgba(this.effectiveStyleData.textColor, false),
                visibility: 'visible',
                pointerEvents: 'auto',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                minWidth: 0,
                maxWidth: '100%'
            };
        },

        getImagePlaceholderStyle(card) {
            const baseStyle = IMAGE_PLACEHOLDER_STYLES;

            const hasIcon = card.imageType === 'icon' && card.selectedIcon;
            const hasImage = (card.imageType === 'image' || card.imageType === 'resource') && card.imageUrl;

            if (hasImage) {
                const zoom = card.zoom || 1;
                const offsetX = card.offsetX || 0;
                const offsetY = card.offsetY || 0;

                return {
                    ...baseStyle,
                    backgroundImage: `url(${card.imageUrl})`,
                    backgroundSize: `${zoom * 100}%`,
                    backgroundPosition: `${50 + offsetX}% ${50 - offsetY}%`,
                    backgroundRepeat: 'no-repeat',
                    border: 'none'
                };
            }

            if (hasIcon) {
                return {
                    ...baseStyle,
                    border: 'none'
                };
            }

            return {
                ...baseStyle,
                border: '2px solid rgba(245, 164, 183, 0.5)'
            };
        },

        focusTitleText(event) {
            if (event.target.classList.contains('title-bar') || event.target.classList.contains('clickable-title')) {
                const el = this.$refs.titleTextRef;
                if (el) {
                    el.focus();
                    const range = document.createRange();
                    const sel = window.getSelection();
                    range.selectNodeContents(el);
                    range.collapse(false);
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
            }
        },

        updateTitle(event) {
            this.updateTextField('titleText', event);
        },

        updateCardButton(index, event) {
            const cards = [...this.menuData.optionCards || []];
            if (!cards[index]) cards[index] = {};
            const element = event.target.querySelector('.btn-text') || event.target;
            const rawText = element.textContent || '';
            const newText = rawText.replace(/\u200B/g, '').trim();
            
            const menuData = {
                ...this.menuData,
                optionCards: cards.map((card, i) => {
                    if (i === index) {
                        return { ...card, buttonText: newText || '\u200B' };
                    }
                    return card;
                })
            };
            this.$emit('update:menu-data', menuData);
        },

        updateCardLabel(index, event) {
            const cards = [...this.menuData.optionCards || []];
            if (!cards[index]) cards[index] = {};
            const element = event.target.querySelector('.label-text') || event.target;
            const rawText = element.textContent || '';
            const newText = rawText.replace(/\u200B/g, '').trim();
            
            const menuData = {
                ...this.menuData,
                optionCards: cards.map((card, i) => {
                    if (i === index) {
                        return { ...card, label: newText || '\u200B' };
                    }
                    return card;
                })
            };
            this.$emit('update:menu-data', menuData);
        },


        isTitleHidden() {
            if (this.isStylePreview || this.isPreview || this.enableInlineEdit) {
                return false;
            }
            return this.isEmptyText(this.menuData.titleText);
        },

        isCardLabelHidden(card) {
            if (this.isStylePreview || this.enableInlineEdit) {
                return false;
            }
            return this.isEmptyText(card.label);
        },

        isCardButtonHidden(card) {
            if (this.isStylePreview || this.enableInlineEdit) {
                return false;
            }
            return this.isEmptyText(card.buttonText);
        },

        shouldShowCardButton(card) {
            if (this.enableInlineEdit) {
                return true;
            }
            return !this.isEmptyText(card.buttonText) || this.isStylePreview;
        },

        handleImageClick(index) {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = (e) => {
                const file = e.target.files[0];
                if (!file) return;
                
                if (file.type && file.type.startsWith('image/')) {
                    this.$emit('upload-image', file, index);
                }
                input.value = '';
            };
            input.click();
        },


        handleCardButtonClick(index, card) {
            if (this.enableInlineEdit) {
                const buttonRef = this.$refs[`buttonRef${index}`];
                const buttonEl = Array.isArray(buttonRef) ? buttonRef[0] : buttonRef;
                if (buttonEl && buttonEl.contentEditable === 'true') {
                    buttonEl.focus();
                }
                return;
            }
            
            if (this.isStylePreview) {
                return;
            }

            this.$emit('button-clicked', index, card);

            if (card.onClick && typeof card.onClick === 'function') {
                card.onClick();
            }

            if (card.action && typeof card.action === 'function') {
                card.action();
            }
        }
    },
};
</script>

<style scoped>
.two-option-large-menu {
    width: 100%;
    height: 100%;
    padding: 20px;
    box-sizing: border-box;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;

    --base-font: 1rem;
    --title-scale: 1.5;
    --card-scale: 1.0;
    --max-width-scale: 1.0;
    --element-scale: 1.0;
    --gap-scale: 1.0;
}

.two-option-large-menu .select {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.two-option-large-menu .select:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 22px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.two-option-large-menu:not(.no-hover) .select:hover {
    background-color: var(--hover-bg) !important;
}

.two-option-large-menu .back-btn {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.two-option-large-menu .back-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.two-option-large-menu:not(.no-hover) .back-btn:hover {
    background-color: var(--hover-bg) !important;
}

.icon-display {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
}

.back-icon-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
}

.back-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0; /* Hide the actual SVG, we'll use the mask instead */
}

.image-display {
    height: 100%;
    width: 100%;
}

@media (max-width: 768px) {
    .frame {
        grid-template-columns: 48px 1fr !important;
    }

    .cards {
        grid-template-columns: 1fr !important;
    }
}

.editable-text.is-editable {
    cursor: text;
    position: relative;
    outline: none;
}

.editable-text.is-editable:hover {
    background-color: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.4);
}

.editable-text.is-editable:focus {
    background-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.7);
}

.title-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    max-height: 3em !important;
    text-align: center !important;
}

.select {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    word-break: break-word !important;
    white-space: normal !important;
    box-sizing: border-box !important;
}

.select .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.select[contenteditable="true"] {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.select[contenteditable="true"] .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.card-label {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    box-sizing: border-box !important;
    min-width: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
}

.card-label .label-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
}

.card-label[contenteditable="true"] {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
}

.card-label[contenteditable="true"] .label-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
}

.image-clickable {
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
}

.image-clickable:hover {
    opacity: 0.95;
}

.image-clickable:not(:has(.image-display)):hover::before {
    content: 'Click to upload image';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(52, 192, 32, 0.9);
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    z-index: 10;
    pointer-events: none;
}

.image-clickable:has(.image-display):hover::after {
    content: 'Click to change';
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(52, 192, 32, 0.9);
    color: white;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    z-index: 10;
    pointer-events: none;
}
</style>
